/** Chats viewer API client: same-origin calls to the admin shell's chats MFE api routes, which the shell middleware forwards to the chats-service worker. */

import { requestFailedWithStatus } from '@federa/const/error-messages';
import { clampOffset } from '@federa/const/config-pagination';
import { fetchTracedEdgeAsBearer } from '@federa/components/lib/api/bearer-edge-fetch';
import {
	buildChatsConversationSubroute,
	CHATS_VIEWER_PAGING,
	CHATS_VIEWER_ROUTES,
	CHATS_VIEWER_SUBROUTES,
} from '@federa/const/chats-viewer-routes';
import { readFirebaseIdToken } from './assistant/ws-credential.ts';
import type { ChatConversationsPage, ChatViewerConversation, ChatViewerMessage, ChatViewerParticipant } from '@federa/types/conversations';

// The viewer page size is the worker's own default clamp (CHATS_VIEWER_PAGING.DEFAULT_PAGE_SIZE), not a client-side choice: sending a different value would make the client's offset arithmetic disagree with the page the worker actually returns.
const PAGE_SIZE = CHATS_VIEWER_PAGING.DEFAULT_PAGE_SIZE;

/**
 * Every viewer call carries the signed-in user's Firebase ID token: the worker gate (`requireG4Viewer` in
 * spa/chats/flow-engine.ts) verifies that JWT and requires a G4+ role. Identity IS the gate, so it has to be on the wire.
 *
 * The token MUST come from the SHARED MFE bridge (`readFirebaseIdToken` -> `readBridgeIdToken`), never from
 * admin-api-utils' `getAuthToken()`: its `getFirebaseUser()` calls `getFirebaseAuth(...)` and so INITIALIZES A SECOND
 * Firebase app in this bundle, which disturbs the shell's auth instance -- `readFirebaseIdToken` then returns null,
 * `resolveWsCredential` returns null, and the assistant SKIPS the WS connect: a permanent "Reconnecting...".
 *
 * A missing token is NOT short-circuited here: the request goes out bare and the worker answers 401. Duplicating the
 * verdict client-side would only add a second place for it to drift.
 *
 * The transport is `fetchTracedEdge`, which adds the originating-edge `traceparent` and the standard timeout and
 * NOTHING ELSE -- in particular it resolves no credential of its own, which is what keeps the paragraph above true.
 *
 * EVERY READER IS `Viewer`-MARKED, and the marker is load-bearing rather than decorative. `components/messaging/messaging.ts`
 * exports a same-shaped `fetchConversations` / `fetchRecentMessages` family that dials the ChatRoom
 * DO's room routes over an INJECTED transport with a guest-or-Firebase credential, and this leaf dials the G4-gated
 * viewer alias routes on the chats worker with the bridge token. Two credentials, two backends, two row shapes, one
 * unqualified name -- the census (`docs/development/reactive-sync-census.md`) recorded three fetchParticipants-shaped
 * symbols for "one concept", which they are not. They cannot fold; naming them apart is what stops the next reader
 * assuming they can.
 *
 * `signal` rides through to `fetchTracedEdge`'s third parameter so react-query cancels a request whose query was
 * dropped, rather than parsing a response into a cache nobody is reading.
 */
async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
	const token = await readFirebaseIdToken();
	const res = await fetchTracedEdgeAsBearer(path, token, signal);
	if (!res.ok) throw new Error(requestFailedWithStatus(res.status));
	const raw: unknown = await res.json();
	return raw as T;
}

/** One page of conversations; `page` is 0-based. */
export function fetchViewerConversationsPage(page: number, signal?: AbortSignal): Promise<ChatConversationsPage> {
	const offset = clampOffset(page) * PAGE_SIZE;
	return apiGet<ChatConversationsPage>(`${CHATS_VIEWER_ROUTES.CONVERSATIONS}?limit=${PAGE_SIZE}&offset=${offset}`, signal);
}

/** The thread (every message) of one conversation -- loaded only when a conversation is opened. */
export function fetchViewerMessages(conversationId: string, signal?: AbortSignal): Promise<ChatViewerMessage[]> {
	return apiGet<{ messages: ChatViewerMessage[] }>(
		buildChatsConversationSubroute(conversationId, CHATS_VIEWER_SUBROUTES.MESSAGES),
		signal,
	).then((r) => r.messages);
}

export function fetchViewerParticipants(conversationId: string, signal?: AbortSignal): Promise<ChatViewerParticipant[]> {
	return apiGet<{ participants: ChatViewerParticipant[] }>(
		buildChatsConversationSubroute(conversationId, CHATS_VIEWER_SUBROUTES.PARTICIPANTS),
		signal,
	).then((r) => r.participants);
}

/** Search conversations by the owning user's username / mfa_phone / email / id. Runs on demand (click). */
export function searchViewerConversations(query: string, signal?: AbortSignal): Promise<ChatViewerConversation[]> {
	return apiGet<{ conversations: ChatViewerConversation[] }>(`${CHATS_VIEWER_ROUTES.SEARCH}?q=${encodeURIComponent(query)}`, signal).then(
		(r) => r.conversations,
	);
}
